import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight, FileArrowDown } from "@phosphor-icons/react";
import { useLanguage } from "../../context/LanguageContext";
import { StampLogo } from "./Wordmark";
import { PDF_URL } from "../../i18n/content";

const HERO_IMG =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMHByZXNlbnRhdGlvbiUyMGJ1c2luZXNzJTIwY29uc3VsdGluZ3xlbnwwfHx8fDE3ODc5MTY2MDh8MA&ixlib=rb-4.1.0&q=85";

const toneClass = {
  ink: "text-brand-ink",
  green: "text-brand-green",
  red: "text-brand-red",
};

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const stampY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} id="hero" data-testid="hero-section" className="relative min-h-screen flex flex-col justify-end overflow-hidden pt-24">
      <div className="max-w-[1440px] mx-auto w-full px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-6 items-end pb-10 md:pb-16">
        {/* Kinetic typography */}
        <div className="lg:col-span-7 order-2 lg:order-1">
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-xs font-bold tracking-[0.25em] uppercase text-black/50 mb-6"
          >
            {t.hero.eyebrow}
          </motion.p>

          <h1 data-testid="hero-headline" className="font-cabinet font-black tracking-tight leading-[0.98] text-4xl sm:text-5xl lg:text-6xl xl:text-7xl">
            {t.hero.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${toneClass[line.tone]}`}
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 1.1, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.85, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-black/60"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-primary"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="group flex items-center gap-3 bg-brand-green text-white px-7 py-4 font-cabinet font-bold text-base tracking-wide hover:bg-brand-ink transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green focus:ring-offset-2"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
            <a
              data-testid="hero-cta-profile"
              href={PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-3 border border-black/25 px-7 py-4 font-cabinet font-bold text-base tracking-wide text-black/80 hover:border-black hover:text-black transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-brand-green"
            >
              {t.hero.ctaSecondary}
              <FileArrowDown size={18} weight="bold" className="transition-transform duration-300 group-hover:translate-y-0.5" />
            </a>
          </motion.div>
        </div>

        {/* Spotlight image with parallax + stamp */}
        <div className="lg:col-span-5 order-1 lg:order-2 relative">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden aspect-[4/3] lg:aspect-[3/4] max-h-[52vh] lg:max-h-[68vh] w-full"
          >
            <motion.img
              src={HERO_IMG}
              alt="DDG consulting session"
              data-testid="hero-image"
              style={{ y: imgY }}
              className="w-full h-[115%] object-cover grayscale"
            />
            <div className="absolute inset-0 bg-brand-green/15 mix-blend-multiply" />
            <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent" />
          </motion.div>
          <motion.div style={{ y: stampY }} className="absolute -bottom-8 -left-8 md:-left-12">
            <StampLogo size={130} className="bg-brand-paper/80 backdrop-blur-sm rounded-full p-2" />
          </motion.div>
        </div>
      </div>

      {/* Stats strip */}
      <motion.div
        data-testid="hero-stats"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="border-t border-black/10"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4">
          {t.hero.stats.map((s, i) => (
            <div
              key={i}
              data-testid={`hero-stat-${i}`}
              className={`py-6 md:py-8 pr-6 ${i !== 0 ? "md:border-l md:border-black/10 md:pl-8" : ""} ${i % 2 !== 0 ? "border-l border-black/10 pl-6 md:pl-8" : ""}`}
            >
              <div className="font-cabinet font-black text-3xl md:text-4xl text-brand-ink">{s.value}</div>
              <div className="mt-1 text-xs font-bold tracking-[0.18em] uppercase text-black/45">{s.label}</div>
            </div>
          ))}
        </div>
        <div className="hidden md:flex absolute bottom-6 right-10 items-center gap-2 text-black/40">
          <span className="text-[10px] font-bold tracking-[0.25em] uppercase">{t.hero.scroll}</span>
          <ArrowDown size={14} className="animate-bounce" />
        </div>
      </motion.div>
    </section>
  );
};
